/**
 * MÓDULOS DO PRODUTO — o que um cliente contrata.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * POR QUE ENUM E NÃO TABELA DE CATÁLOGO
 * ─────────────────────────────────────────────────────────────────────────────
 * A tentação é uma tabela `module` administrável pelo console. Ela seria errada:
 * um módulo só existe se houver código que o implemente, e uma linha nova numa
 * tabela não escreve código. O console poderia então vender ao cliente um módulo
 * que não existe, e a falha apareceria como 404 na tela dele.
 *
 * O conjunto de módulos muda quando o produto muda — ou seja, num deploy. Por
 * isso ele mora aqui, ao lado das rotas que cada um governa, e uma adição
 * aparece no diff e na revisão.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * O QUE NÃO É CONTRATÁVEL, E POR QUÊ
 * ─────────────────────────────────────────────────────────────────────────────
 * Autenticação, usuários, setores, configuração da organização, assinatura
 * eletrônica e trilha de auditoria NÃO aparecem nesta lista, e a ausência é
 * deliberada: são o que torna o sistema conforme, não o que se vende dentro
 * dele. Um cliente com o GED e sem trilha de auditoria teria um sistema que
 * falha inspeção — vender essa combinação seria vender uma não-conformidade.
 *
 * O bloqueio real acontece no gateway (`route-table.ts`), que é onde a
 * autorização já mora. A interface esconder o menu é conveniência; não é
 * controle.
 */
/** Um módulo contratável. O valor é estável: vai para o banco e para o JWT. */
export const ProductModule = {
    /** Documentos, tipos de documento, versões, aprovação, distribuição e ciência. */
    GED: 'GED',
    /** Não-conformidade: abertura, GUT, avaliação de procedência e encerramento. */
    NONCONFORMITY: 'NONCONFORMITY',
    /** Solicitações e plano de ação: Ishikawa, 5W2H, execução e eficácia. */
    CAPA: 'CAPA',
    /** Reuniões, catálogo de tipos, ata assinada e SACAP. */
    MEETINGS: 'MEETINGS',
    /** Circulares: comunicado numerado com matriz de leitura e ciência assinada. */
    CIRCULARS: 'CIRCULARS',
    /** Indicadores do SGQ (ISO 13485 §5.6) e sua exportação. */
    INDICATORS: 'INDICATORS',
};
export const PRODUCT_MODULE_LABEL_PT_BR = {
    GED: 'GED — Gestão eletrônica de documentos',
    NONCONFORMITY: 'Não-conformidade',
    CAPA: 'Solicitações e plano de ação',
    MEETINGS: 'Reuniões',
    CIRCULARS: 'Circulares',
    INDICATORS: 'Indicadores do SGQ',
};
/**
 * Uma linha por módulo, dita em uma frase, para a tela de contratação.
 *
 * Mora aqui e não no frontend porque quem adiciona um módulo precisa ser
 * obrigado a descrevê-lo: o `Record` completo faz o TypeScript recusar a
 * omissão. Descrição esquecida vira célula vazia na tela de vendas.
 */
export const PRODUCT_MODULE_SUMMARY_PT_BR = {
    GED: 'Documento controlado com versão, aprovação, distribuição e ciência registrada.',
    NONCONFORMITY: 'Registro de desvio com matriz GUT, avaliação de procedência e encerramento verificado.',
    CAPA: 'Ação corretiva a partir de causa-raiz, plano 5W2H e verificação de eficácia.',
    MEETINGS: 'Agenda, condução, ata assinada e SACAP de acompanhamento.',
    CIRCULARS: 'Comunicado numerado com matriz de leitura e ciência assinada.',
    INDICATORS: 'Painel dos indicadores da qualidade e exportação para a análise crítica.',
};
/**
 * DEPENDÊNCIAS ENTRE MÓDULOS.
 *
 * Não é preferência comercial: é o que o código faz. A NC só encerra depois que
 * `assertCapaIsComplete` confirma que TODA ação de tratamento terminou com
 * eficácia aprovada — e essas ações são do módulo CAPA. Vender NC sem CAPA
 * entrega ao cliente um fluxo que aceita abrir e avaliar a não-conformidade e
 * depois não tem como encerrá-la: ela fica presa em `ACCEPTED` para sempre,
 * porque a tela que cria a ação de tratamento devolve 402.
 *
 * O console recusa a combinação em vez de deixar o cliente descobrir sozinho,
 * meses depois, com a primeira NC procedente.
 */
export const PRODUCT_MODULE_REQUIRES = {
    NONCONFORMITY: [ProductModule.CAPA],
};
/**
 * O que falta para que este conjunto seja coerente.
 *
 * Devolve as dependências ausentes, com o dependente junto, para que a mensagem
 * de erro diga QUEM exige o quê — "falta CAPA" sozinho não explica nada a quem
 * está montando o contrato.
 */
export function missingModuleDependencies(selected) {
    const conjunto = new Set(selected);
    const faltantes = [];
    for (const module of selected) {
        for (const requires of PRODUCT_MODULE_REQUIRES[module] ?? []) {
            if (!conjunto.has(requires))
                faltantes.push({ module, requires });
        }
    }
    return faltantes;
}
/**
 * O conjunto contratado por padrão ao cadastrar um cliente novo.
 *
 * Todos. Um cliente nasce com o produto inteiro e o vendedor DESMARCA o que não
 * foi contratado — o contrário faria um cliente recém-criado abrir a tela vazia,
 * e o suporte descobriria isso pela reclamação dele.
 */
export const DEFAULT_CONTRACTED_MODULES = Object.values(ProductModule);
//# sourceMappingURL=product-modules.js.map